import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import {
  appNameFor,
  destinationFor,
  flattenMemberships,
  labelFor,
  membershipId,
} from './shared/memberships';
import { AppShell } from './components/AppShell';
import { PageHead } from './components/ui';
import { Login } from './pages/Login';
import { Home } from './pages/Home';
import { People } from './pages/People';
import { Money } from './pages/Money';
import { Games } from './pages/Games';
import { BooksHub } from './pages/books/BooksHub';
import { Help } from './pages/Help';
import { Todos } from './pages/Todos';
import { CurriculumReview } from './pages/CurriculumReview';
import { Studios } from './pages/Studios';
import { StudioDetail } from './pages/StudioDetail';
import { lazy, Suspense } from 'react';

// The lesson editor carries TipTap; it loads only when Lessons opens.
const Lessons = lazy(() => import('./pages/Lessons').then((m) => ({ default: m.Lessons })));
const ModuleLessons = lazy(() => import('./pages/Lessons').then((m) => ({ default: m.ModuleLessons })));
const LessonEditorPage = lazy(() => import('./pages/Lessons').then((m) => ({ default: m.LessonEditorPage })));
import { Illustrations } from './pages/Illustrations';
import { MarketingOverview } from './pages/marketing/Overview';
import { MarketingQueue } from './pages/marketing/Queue';
import { MarketingInbox } from './pages/marketing/Inbox';
import { MarketingSettings } from './pages/marketing/Settings';

/**
 * Signed in, but not an admin. Instead of a dead end, route the person to the
 * app their account actually belongs to (memberships from app_my_memberships).
 */
function Forbidden() {
  const { user, memberships, signOut } = useAuth();
  const items = memberships
    ? flattenMemberships(memberships).filter((m) => m.kind !== 'admin')
    : [];
  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-eyebrow">Rootegra · admin</div>
        <h1 className="login-h1">This is the admin console</h1>
        <p className="login-sub">
          This is the Rootegra admin console. You're signed in as{' '}
          <strong>{user?.email}</strong>, which isn't on the admin list.
          {items.length > 0 && ' Your account lives here instead:'}
        </p>
        {items.length > 0 && (
          <div className="route-list">
            {items.map((m) => (
              <a key={membershipId(m)} className="route-row" href={destinationFor(m)}>
                <span className="route-label">{labelFor(m)}</span>
                <span className="route-go">Go to {appNameFor(m)} →</span>
              </a>
            ))}
          </div>
        )}
        {memberships !== null && items.length === 0 && (
          <p className="login-sub">
            We couldn't find any studio or client account for this email either. If
            that seems wrong, reach out to your studio.
          </p>
        )}
        {memberships === null && (
          <p className="login-sub">
            We couldn't load your account details just now — try again in a moment.
          </p>
        )}
        <button className="btn" onClick={() => void signOut()}>Sign out</button>
      </div>
    </div>
  );
}

/** Placeholder until the Illustrations page lands (owned by another build). */
function IllustrationsSoon() {
  return (
    <>
      <PageHead title="Illustrations" sub="This page is being built. Pictures missing from lessons will be listed here, with a work order to copy into ChatGPT and a drop zone for the finished picture." />
    </>
  );
}

export default function App() {
  const { loading, user, authorized, booksOnly } = useAuth();
  if (loading) return <div className="login-wrap"><div className="muted">Loading…</div></div>;
  if (!user) return <Routes><Route path="*" element={<Login />} /></Routes>;
  if (!authorized) return <Forbidden />;

  // The outside accountant: a books_access row and nothing else. Books and
  // Help are the whole console; every other address lands on Books.
  if (booksOnly) {
    return (
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/books" element={<BooksHub />} />
          <Route path="/books/:tab" element={<BooksHub />} />
          <Route path="/help" element={<Help />} />
          <Route path="*" element={<Navigate to="/books" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<Home />} />
        <Route path="/people" element={<People />} />
        <Route path="/money" element={<Money />} />
        <Route path="/games" element={<Games />} />
        <Route path="/books" element={<BooksHub />} />
        <Route path="/books/:tab" element={<BooksHub />} />
        <Route path="/help" element={<Help />} />
        <Route path="/lessons" element={<Suspense fallback={<div className="muted">Loading…</div>}><Lessons /></Suspense>} />
        <Route path="/lessons/:moduleId" element={<Suspense fallback={<div className="muted">Loading…</div>}><ModuleLessons /></Suspense>} />
        <Route path="/lessons/:moduleId/:lessonId" element={<Suspense fallback={<div className="muted">Loading…</div>}><LessonEditorPage /></Suspense>} />
        <Route path="/illustrations" element={<Illustrations />} />
        <Route path="/marketing" element={<MarketingOverview />} />
        <Route path="/marketing/queue" element={<MarketingQueue />} />
        <Route path="/marketing/inbox" element={<MarketingInbox />} />
        <Route path="/marketing/settings" element={<MarketingSettings />} />
        {/* Older pages: off the menu, still reachable by URL. */}
        <Route path="/todos" element={<Todos />} />
        <Route path="/curriculum" element={<CurriculumReview />} />
        <Route path="/studios" element={<Studios />} />
        <Route path="/studios/:salonId" element={<StudioDetail />} />
        <Route path="/accounting" element={<Navigate to="/books" replace />} />
        <Route path="/users" element={<Navigate to="/people" replace />} />
        <Route path="/subscriptions" element={<Navigate to="/money" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
